import { z } from "zod";
import { AlgorithmId, CampaignPhaseResult } from "@algoquest/schemas";
import { LevelProgressDTOSchema } from "./progress";

/**
 * `attempts` domain DTOs (Master Spec §19/§31/§33/§40, Phase 10).
 *
 * `attemptId` is generated by the CLIENT (a UUID, minted the moment a
 * level completes — see `game/src/services/db/localDb.ts`) and doubles
 * as `game_attempts.id` server-side (see that table's Phase 10 doc
 * comment in `@algoquest/database`), which is what makes a replayed sync
 * after a dropped connection idempotent rather than a duplicate insert.
 *
 * The metric fields mirror `game_attempts` 1:1 rather than nesting a
 * separate "metrics" object, matching the flat shape the Algorithm
 * Engines already expose on `SearchViewState`/`SortViewState` (Phase 04/
 * 08) — the client reads these fields directly off the completed view,
 * no reshaping needed. `found`/`swaps`/`shifts`/`passes` are algorithm-
 * shaped: a Searching attempt always sends `swaps: 0, shifts: 0,
 * passes: 0`; a Sorting attempt always sends `found: null` — matching
 * `game_attempts`' own nullable/defaulted columns exactly.
 */
export const SubmitAttemptRequestSchema = z.object({
  attemptId: z.string().uuid(),
  globalLevelNumber: z.number().int().min(1).max(100),
  algorithm: AlgorithmId,
  strategyReason: z.enum(['linear-any','binary-halves','bubble-neighbours','selection-swaps','insertion-prefix','insertion-nearly','always-fastest']).optional(),
  phaseResults: z.array(CampaignPhaseResult).min(1).max(2).optional(),
  found: z.number().int().nonnegative().nullable().default(null),
  comparisons: z.number().int().nonnegative(),
  swaps: z.number().int().nonnegative().default(0),
  shifts: z.number().int().nonnegative().default(0),
  passes: z.number().int().nonnegative().default(0),
  mistakes: z.number().int().nonnegative().default(0),
  hints: z.number().int().nonnegative().default(0),
  durationMs: z.number().int().nonnegative(),
});
export type SubmitAttemptRequest = z.infer<typeof SubmitAttemptRequestSchema>;

export const AttemptDTOSchema = z.object({
  reasoningScore:z.number().int().min(0).max(100).optional(),
  id: z.string().uuid(),
  levelId: z.string().uuid(),
  globalLevelNumber: z.number().int().min(1).max(100),
  algorithm: AlgorithmId,
  strategyReason: z.enum(['linear-any','binary-halves','bubble-neighbours','selection-swaps','insertion-prefix','insertion-nearly','always-fastest']).optional(),
  phaseResults: z.array(CampaignPhaseResult).min(1).max(2).optional(),
  found: z.number().int().nonnegative().nullable(),
  comparisons: z.number().int().nonnegative(),
  swaps: z.number().int().nonnegative(),
  shifts: z.number().int().nonnegative(),
  passes: z.number().int().nonnegative(),
  mistakes: z.number().int().nonnegative(),
  hints: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
});
export type AttemptDTO = z.infer<typeof AttemptDTOSchema>;

/**
 * Returns the resulting `level_progress` row alongside the stored
 * attempt so the client can update its local progress cache immediately
 * from the sync response, without a separate `GET /progress` round trip
 * for the common case of "I just synced one attempt."
 */
export const SubmitAttemptResponseSchema = z.object({
  attempt: AttemptDTOSchema,
  progress: LevelProgressDTOSchema,
});
export type SubmitAttemptResponse = z.infer<typeof SubmitAttemptResponseSchema>;

/**
 * Phase 11 (Master Spec §34 STUDENT "history") — `GET /api/v1/attempts`.
 * Deliberately flat/unpaginated for now (server caps at 50 rows, most-
 * recent-first — see `attempts.repository.ts`'s `listAttemptsForUser`);
 * real pagination is a candidate for whenever a UI actually needs more
 * than the default page.
 */
export const ListAttemptsResponseSchema = z.object({
  attempts: z.array(AttemptDTOSchema),
});
export type ListAttemptsResponse = z.infer<typeof ListAttemptsResponseSchema>;
